import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { OrgNode } from '../../api/structure';

interface OrganigrammeNodeProps {
  node: OrgNode;
  level?: number;
  defaultCollapsed?: boolean;
}

const LEVEL_COLORS = ['#0EA5E9', '#0284C7', '#0284C7', '#10B981', '#F59E0B'];

const OrganigrammeNode: React.FC<OrganigrammeNodeProps> = ({
  node,
  level = 0,
  defaultCollapsed = false,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed && level > 1);
  const nodeColor = LEVEL_COLORS[level % LEVEL_COLORS.length];
  const hasChildren = node.children.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          background: 'var(--color-surface)',
          border: `2px solid ${nodeColor}30`,
          borderRadius: 'var(--radius-md)',
          padding: '0.75rem 1rem',
          minWidth: 180,
          maxWidth: 220,
          position: 'relative',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        {level > 0 && (
          <div
            style={{
              position: 'absolute',
              top: -1,
              left: '50%',
              transform: 'translateX(-50%) translateY(-100%)',
              width: 2,
              height: 20,
              background: 'var(--color-border)',
            }}
          />
        )}

        <p style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', margin: 0, color: nodeColor }}>
          {node.nom || 'Sans nom'}
        </p>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', margin: '0.125rem 0 0' }}>
          {node.poste || 'Sans poste'}
        </p>
        {node.department && (
          <p style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
            {node.department}
          </p>
        )}

        {hasChildren && (
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.2rem',
              marginTop: '0.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              fontSize: '0.65rem',
              padding: 0,
              alignSelf: 'center',
            }}
          >
            {collapsed ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />}
            {node.children.length} collaborateur{node.children.length > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {!collapsed && hasChildren && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            marginTop: 20,
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          {node.children.length > 1 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '15%',
                right: '15%',
                height: 2,
                background: 'var(--color-border)',
              }}
            />
          )}
          {node.children.map((child: OrgNode) => (
            <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <OrganigrammeNode node={child} level={level + 1} defaultCollapsed={defaultCollapsed} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface OrganigrammeViewerProps {
  organigramme: OrgNode;
  defaultCollapsed?: boolean;
}

const OrganigrammeViewer: React.FC<OrganigrammeViewerProps> = ({
  organigramme,
  defaultCollapsed = false,
}) => (
  <div style={{ overflowX: 'auto', padding: '1rem 0' }}>
    <div style={{ display: 'flex', justifyContent: 'center', minWidth: 'min-content' }}>
      <OrganigrammeNode node={organigramme} level={0} defaultCollapsed={defaultCollapsed} />
    </div>
  </div>
);

export default OrganigrammeViewer;
